/**
 * GeoDirectory Maps
 *
 * Modern modular map system for GeoDirectory.
 * Supports Google Maps and OpenStreetMap (Leaflet).
 */

// Core classes
import { MapProvider } from './core/MapProvider.js';
import { GoogleMapProvider } from './core/GoogleMapProvider.js';
import { OSMMapProvider } from './core/OSMMapProvider.js';
import { MapFactory } from './core/MapFactory.js';

// Features
import { Geocoding } from './features/Geocoding.js';
import { CountryRules } from './features/CountryRules.js';
import { AddressField } from './features/AddressField.js';
import { Popup } from './features/Popup.js';
import { MapInitializer } from './MapInitializer.js';
import { LazyLoader } from './LazyLoader.js';
import { DynamicScriptLoader } from './DynamicScriptLoader.js';
import { MarkerManager } from './MarkerManager.js';
import { MapFilters } from './MapFilters.js';

// Export everything under GeoDir.Maps namespace
const Maps = {
	// Core
	MapProvider,
	GoogleMapProvider,
	OSMMapProvider,
	MapFactory,

	// Features
	Geocoding,
	CountryRules,
	AddressField,
	Popup,
	MapInitializer,
	LazyLoader,
	DynamicScriptLoader,
	MarkerManager,
	MapFilters,

	// Convenience methods
	createMap(elementId, options = {}) {
		const provider = MapFactory.create(elementId, options);
		if (provider) {
			provider.init();
		}
		return provider;
	},

	getAvailableProvider() {
		return MapFactory.getAvailableProvider();
	},

	isGoogleAvailable() {
		return MapFactory.isGoogleMapsAvailable();
	},

	isOSMAvailable() {
		return MapFactory.isOSMAvailable();
	},

	// Modern map initialization methods
	initAjaxSearchMap(mapCanvas, loadTerms = true, options = {}) {
		return MapInitializer.initAjaxSearchMap(mapCanvas, loadTerms, options);
	},

	initStaticMap(mapCanvas, options = {}) {
		return MapInitializer.initStaticMap(mapCanvas, options);
	},

	initDirections(mapCanvas) {
		return MapInitializer.initDirections(mapCanvas);
	}
};

// Attach to window for global access
window.GeoDir = window.GeoDir || {};
window.GeoDir.Maps = Maps;


// Also export for ES6 module usage
export default Maps;
export {
	MapProvider,
	GoogleMapProvider,
	OSMMapProvider,
	MapFactory,
	Geocoding,
	CountryRules,
	AddressField,
	Popup,
	MapInitializer,
	LazyLoader,
	DynamicScriptLoader,
	MarkerManager,
	MapFilters
};
